<template>
  <div class="pa-6">
    <h1 class="mb-5">Subscription Keys</h1>

    <v-autocomplete
      v-model="selectedCompany"
      :items="filteredCompanies"
      item-text="name"
      item-value="id"
      label="Select Company"
      class="mb-6"
      required
      :loading="$apollo.loading"
      :search-input.sync="search"
      return-object
      clearable
    ></v-autocomplete>
    <!-- "Backup / Default klíč" lists/creates the global fallback subscription key
         (companyId = null), used when a company has no valid key of its own. -->

    <subscription-key-form
      v-model="prices"
      @create="createSubscriptionKey"
      :latest-active-from="latestActiveFrom"
      :is-backup-key="isBackupKeySelected"
      :can-create="hasCompanyContext"
    />

    <h2>Subscription Keys</h2>

    <v-card class="mb-6 pa-4">
      <div v-if="isLoading" class="d-flex justify-center mt-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <v-data-table v-else :items="subscriptionKeys" :headers="dynamicHeaders" item-value="id">
        <template v-slot:item.companyName="{ item }">
          <v-chip v-if="!item.companyName" color="secondary" small>Backup / Default</v-chip>
          <span v-else>{{ item.companyName }}</span>
        </template>
        <template v-slot:item.activeFrom="{ item }">
          {{ formatDate(item.activeFrom) }}
        </template>
        <template v-slot:item.isActive="{ item }">
          <v-icon v-if="isActive(item)">mdi-check</v-icon>
        </template>
        <template v-slot:item.delete="{ item }">
          <v-btn color="error" small :disabled="!canDelete(item)" @click="handleDelete(item)">
            Delete
          </v-btn>
        </template>
        <template v-slot:item.CZK="{ item }">
          <div>
            <span>{{ formatPrice(getPriceByCurrency(item.prices, 'CZK')) }}</span>
            <div
              v-if="getWholesalePriceByCurrency(item.prices, 'CZK') !== null"
              class="text-caption grey--text"
            >
              Wholesale: {{ formatPrice(getWholesalePriceByCurrency(item.prices, 'CZK')) }}
            </div>
          </div>
        </template>
        <template v-slot:item.PLN="{ item }">
          <div>
            <span>{{ formatPrice(getPriceByCurrency(item.prices, 'PLN')) }}</span>
            <div
              v-if="getWholesalePriceByCurrency(item.prices, 'PLN') !== null"
              class="text-caption grey--text"
            >
              Wholesale: {{ formatPrice(getWholesalePriceByCurrency(item.prices, 'PLN')) }}
            </div>
          </div>
        </template>
        <template v-slot:item.USD="{ item }">
          <div>
            <span>{{ formatPrice(getPriceByCurrency(item.prices, 'USD')) }}</span>
            <div
              v-if="getWholesalePriceByCurrency(item.prices, 'USD') !== null"
              class="text-caption grey--text"
            >
              Wholesale: {{ formatPrice(getWholesalePriceByCurrency(item.prices, 'USD')) }}
            </div>
          </div>
        </template>
        <template v-slot:item.EUR="{ item }">
          <div>
            <span>{{ formatPrice(getPriceByCurrency(item.prices, 'EUR')) }}</span>
            <div
              v-if="getWholesalePriceByCurrency(item.prices, 'EUR') !== null"
              class="text-caption grey--text"
            >
              Wholesale: {{ formatPrice(getWholesalePriceByCurrency(item.prices, 'EUR')) }}
            </div>
          </div>
        </template>
        <template v-slot:item.autoStartPrepaidPeriod="{ item }">
          <v-icon v-if="item.autoStartPrepaidPeriod">mdi-check</v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { subscriptionKeys } from '@/graphql/query/subscriptionKeys'
import { subscriptionKeyCreate } from '@/graphql/mutations/subscriptionKeyCreate'
import { companies } from '@/graphql/query/companies'
import { subscriptionKeyDelete } from '../../graphql/mutations/subscriptionKeyDelete'
import SubscriptionKeyForm from '../../components/Admin/SubscriptionKeyForm.vue'

// "companyId" semantics for the subscriptionKeys query / subscriptionKeyCreate mutation:
//  - undefined (ALL_COMPANIES / nothing selected) -> no filter, list/create is not allowed
//  - null (BACKUP_COMPANY)                        -> the global backup/default key
//  - "<id>" (a real company)                      -> that company's keys
const ALL_COMPANIES = { id: undefined, name: 'Všechny' }
const BACKUP_COMPANY = { id: null, name: 'Backup / Default klíč' }

export default {
  components: {
    SubscriptionKeyForm,
  },
  data() {
    return {
      selectedCompany: ALL_COMPANIES,
      isLoading: false,
      companies: [],
      companiesQueryInput: {
        search: null,
        first: 200,
        after: null,
        last: null,
        before: null,
      },
      search: '',
      datePicker: false,
      newKey: {
        price: '',
        currency: 'CZK',
        subscriptionLength: '',
        activeFrom: '',
      },
      subscriptionKeys: [],
      prices: [{ price: 1, currency: 'CZK' }],
    }
  },
  computed: {
    formattedDate() {
      return this.newKey.activeFrom ? new Date(this.newKey.activeFrom).toLocaleDateString() : ''
    },
    companiesData() {
      if (!this.companies || !this.companies.edges) {
        return []
      }
      return this.companies.edges.map((edge) => edge.node)
    },
    filteredCompanies() {
      const companies = !this.search
        ? this.companiesData
        : this.companiesData.filter((company) =>
            company.name.toLowerCase().includes(this.search.toLowerCase()),
          )
      return [ALL_COMPANIES, BACKUP_COMPANY, ...companies]
    },
    isBackupKeySelected() {
      return this.selectedCompany === BACKUP_COMPANY
    },
    hasCompanyContext() {
      return !!this.selectedCompany && this.selectedCompany !== ALL_COMPANIES
    },
    latestActiveFrom() {
      // With "Všechny" selected, subscriptionKeys holds every company's + the backup key mixed
      // together, so its latest date isn't a meaningful "existing key" comparison for a new key.
      if (!this.hasCompanyContext || !this.subscriptionKeys.length) return null

      return new Date(parseInt(this.subscriptionKeys[0].activeFrom))
    },
    minDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
    dynamicHeaders() {
      return [
        { text: 'ID', value: 'id' },
        { text: 'Company Name', value: 'companyName' },
        { text: 'Length (months)', value: 'subscriptionLength' },
        { text: 'Active From', value: 'activeFrom' },
        { text: 'Is Active', value: 'isActive' },
        { text: 'Delete', value: 'delete', sortable: false },
        { text: 'CZK', value: 'CZK' },
        { text: 'PLN', value: 'PLN' },
        { text: 'USD', value: 'USD' },
        { text: 'EUR', value: 'EUR' },
        { text: 'FOC length', value: 'initialFocDays' },
        { text: 'FOC length', value: 'prepaidPeriodDays' },
        { text: 'Auto start', value: 'autoStartPrepaidPeriod' },
      ]
    },
  },
  apollo: {
    companies: {
      query: companies,
      variables() {
        return this.companiesQueryInput
      },
    },
    subscriptionKeys: {
      query: subscriptionKeys,
      variables() {
        // ALL_COMPANIES.id is undefined -> variable omitted -> query returns every key.
        // BACKUP_COMPANY.id is null -> explicit companyId: null -> only the backup key.
        // A real company's id -> only that company's keys.
        return {
          companyId: this.selectedCompany?.id,
        }
      },
      update(data) {
        // Sort subscription keys by activeFrom date
        const keys = [...(data.subscriptionKeys || [])]
        return keys.sort((a, b) => parseInt(b.activeFrom) - parseInt(a.activeFrom)) // Sort in descending order
      },
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      try {
        // Handle Unix timestamp in milliseconds
        const d = typeof date === 'number' ? new Date(date) : new Date(parseInt(date))
        if (isNaN(d.getTime())) {
          return ''
        }

        // Format as dd-MM-yyyy
        const day = String(d.getDate()).padStart(2, '0')
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const year = d.getFullYear()

        return `${day}-${month}-${year}`
      } catch (error) {
        return ''
      }
    },
    async handleDelete(item) {
      try {
        const input = {
          id: item.id,
        }
        const { data } = await this.$apollo.mutate({
          mutation: subscriptionKeyDelete,
          variables: { input },
        })

        if (data.subscriptionKeyDelete.success) {
          // Refresh the subscription keys list
          await this.$apollo.queries.subscriptionKeys.refetch()

          this.$toast.success('Subscription key deleted successfully')
        } else {
          this.$toast.error('Failed to delete subscription key')
        }
      } catch (error) {
        this.$toast.error('Error deleting subscription key')
      }
    },
    canDelete(item) {
      const now = new Date()

      const activeFrom = new Date(parseInt(item.activeFrom))
      return activeFrom > now
    },
    async createSubscriptionKey(subscriptionData) {
      if (!this.selectedCompany || this.selectedCompany === ALL_COMPANIES) {
        this.$toast.error('Please select a company or "Backup / Default klíč" first')
        return
      }

      // Map prices to integers in cents
      const mappedPrices = subscriptionData.prices.map((price) => {
        let priceInCents = Math.round(price.price * 100) // Convert to cents
        // wholesalePrice is stored the same way as price (cents, no conversion server-side) -
        // optional, so only convert it when the admin actually entered one.
        let wholesalePriceInCents =
          price.wholesalePrice !== null && price.wholesalePrice !== ''
            ? Math.round(price.wholesalePrice * 100)
            : null
        // Handle currency-specific rules
        if (price.currency === 'HUF') {
          // Ensure price ends with 00 for HUF
          priceInCents = Math.floor(priceInCents / 100) * 100 // Round down to nearest hundred
          if (wholesalePriceInCents !== null) {
            wholesalePriceInCents = Math.floor(wholesalePriceInCents / 100) * 100
          }
        }
        return {
          price: priceInCents,
          currency: price.currency,
          wholesalePrice: wholesalePriceInCents,
        }
      })

      const input = {
        // subscriptionKeyCreate creates/replaces the global backup key when companyId is
        // omitted entirely - it must not be sent as an explicit null.
        ...(this.selectedCompany.id ? { companyId: this.selectedCompany.id } : {}),
        prices: mappedPrices,
        subscriptionLength: parseFloat(subscriptionData.subscriptionLength),
        activeFrom: subscriptionData.activeFrom,
        initialFocDays: subscriptionData.initialFocDays,
        prepaidPeriodDays: subscriptionData.prepaidPeriodDays,
        autoStartPrepaidPeriod: subscriptionData.autoStartPrepaidPeriod,
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: subscriptionKeyCreate,
          variables: { input },
        })

        if (data.subscriptionKeyCreate.success) {
          this.$toast.success('Subscription key created successfully')
          // Optionally, refresh the subscription keys list
          await this.$apollo.queries.subscriptionKeys.refetch()
        } else {
          this.$toast.error('Failed to create subscription key')
        }
      } catch (error) {
        this.$toast.error('Error creating subscription key')
      }
    },
    isActive(item) {
      const itemDate = new Date(parseInt(item.activeFrom))
      const currentDate = new Date()

      // Check if this is the latest active key
      const isLatestActive = this.subscriptionKeys.every((key) => {
        const keyDate = new Date(parseInt(key.activeFrom))
        return keyDate <= itemDate || keyDate > currentDate
      })

      return isLatestActive && itemDate <= currentDate
    },
    getPriceByCurrency(prices, currency) {
      const priceObj = prices.find((price) => price.currency === currency)
      return priceObj ? priceObj.price : 0 // Return 0 if no price found for the currency
    },
    getWholesalePriceByCurrency(prices, currency) {
      const priceObj = prices.find((price) => price.currency === currency)
      return priceObj?.wholesalePrice ?? null
    },
    formatPrice(priceInCents) {
      console.log(priceInCents)
      const price = priceInCents / 100 // Convert cents to the main currency unit
      return `${price.toFixed(2)}` // Format to two decimal places
    },
  },
}
</script>

<style scoped></style>
